"use client";

import { Button, Heading, Text, TextArea } from "@afa-shared/afa-components";
import { FormProps } from "@components/FormBlock";
import { StandardLink } from "@components/Link/StandardLink";
import Grid from "@components/client/Grid";
import clsx from "clsx";
import React, { useId, useRef, useState } from "react";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";

import FormErrorFeedback from "../Feedback/FormErrorFeedback";
import { FormSendFeedback } from "../Feedback/FormSendFeedback";
import FormSuccessFeedback from "../Feedback/FormSuccessFeedback";
import formStyles from "../FormStyles/forms.module.css";
import { scrollIntoView, validate } from "../formHelper";

export const AccessibilityForm = ({ contentfulPreviewEntryId }: FormProps) => {
  const personuppgifterPath = "/personuppgifter";
  const [formState, setFormState] = useState({
    accessibilityFormMessage: { value: "", required: true },
  });

  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState<Record<string, any>>({
    accessibilityFormMessage: null,
  });

  const [displayForm, setDisplayForm] = useState<boolean>(true);
  const [isValidToken, setIsValidToken] = useState<boolean>(false);
  const [unabletoSendMail, setUnabletoSendMail] = useState<boolean>(false);

  const formRefs = {
    sendFeedback: useRef<HTMLDivElement>(null),
    accessibilityFormMessage: useRef<HTMLTextAreaElement>(null),
  };

  const formIds = {
    header: useId(),
    accessibilityFormMessage: useId(),
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const { value } = e.target;
    setFormState({ ...formState, [key]: { ...formState[key], value: value } });
    setErrors({ ...errors, [key]: null });
  };

  const handleErrorResponse = (): void => {
    setUnabletoSendMail(true);
    setDisplayForm(false);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formErrors = validate(formState, errors);
    setErrors(formErrors);
    const foundErrors = Object.values(formErrors).filter((e) => e !== null).length;

    if (foundErrors) {
      const key = Object.keys(formErrors).find((key) => formErrors[key] !== null);
      if (key) scrollIntoView(formRefs[key]);
    } else {
      let cleanFormState = {};
      for (const field in formState) {
        cleanFormState = { ...cleanFormState, [field]: formState[field].value };
      }

      setIsLoading(true);
      try {
        const res = await fetch("/api/sendAccessibilityInputMail", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(cleanFormState),
        });

        if (res?.ok) {
          setDisplayForm(false);
          formRefs?.sendFeedback?.current?.focus();
        } else {
          handleErrorResponse();
        }

        setIsLoading(false);
      } catch (err) {
        console.log("error", err);
      }
    }
  };

  const handleReCaptchaVerify = (token) => {
    if (!token) {
      return;
    }
    token && setIsValidToken(true);
  };

  const sentMailSuccessfully: boolean = !displayForm && !unabletoSendMail;

  return (
    <Grid
      direction={"column"}
      className={`form-container`}
      data-contentful-entry-id={contentfulPreviewEntryId}
      data-contentful-field-id="name"
    >
      <GoogleReCaptcha onVerify={(token) => handleReCaptchaVerify(token)} />
      {
        <FormSendFeedback
          formSendRef={formRefs?.sendFeedback}
          showErrorFeedback={unabletoSendMail}
          formErrorFeedback={<FormErrorFeedback />}
          showSuccessFeedback={sentMailSuccessfully}
          formSuccessFeedback={
            <FormSuccessFeedback
              messageHeading="Din information har skickats"
              messageChildren={<Text variant="paragraph" children={"Tack för dina synpunkter."} />}
            />
          }
        />
      }
      {displayForm && (
        <div className={`${formStyles.formBackground} ${formStyles.formWrapper}`}>
          <div className={formStyles.formHeadingWrapper}>
            <Heading id={formIds?.header} variant="h3" removeMargin>
              Ditt meddelande
            </Heading>
            <Text variant="paragraph" removeMargin>
              * Obligatoriskt fält
            </Text>
          </div>
          <form
            aria-labelledby={formIds?.header}
            noValidate
            className={formStyles.accessibilityForm}
          >
            <div className={formStyles.formStep}>
              <TextArea
                label={"Beskriv hur du upplever brister inom tillgänglighet*"}
                description="Skriv inte personnummer eller känsliga uppgifter här."
                textAreaRef={formRefs?.accessibilityFormMessage}
                required
                id={formIds?.accessibilityFormMessage}
                value={formState?.accessibilityFormMessage?.value}
                data-testid="accessibilityFeedbackMessage"
                name="accessibilityFormMessage"
                autoComplete="accessibilityFormMessage"
                type={"text"}
                maxLength={1000}
                width={"100%"}
                rows={8}
                error={errors?.accessibilityFormMessage?.input}
                onChange={(e) => handleFormChange(e, "accessibilityFormMessage")}
              />
            </div>
            <StandardLink href={personuppgifterPath} text={"Så behandlar vi personuppgifter"} />
            <div className={formStyles.accessibilityFormButtonWrapper}>
              <Button
                type="submit"
                buttonType="primary"
                onClick={
                  isValidToken
                    ? onSubmit
                    : (e) => {
                        e.preventDefault();
                      }
                }
                label={isLoading ? "" : "Skicka"}
                className={clsx({ [formStyles.spinner]: isLoading })}
              />
            </div>
          </form>
        </div>
      )}
    </Grid>
  );
};
export default AccessibilityForm;
